import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { motion, MotionConfig, useScroll, useTransform, useAnimation, useReducedMotion } from 'framer-motion';
import { useCookieModalContext } from '@starlingbank/cookie-modal';
import { useMediumUpBp } from '../../hooks/useMedia';
import CTAButton from '../ctaButton/CTAButton';
import { data, bezierCurve, transformScale } from './data';
import * as styles from './TravelHero.module.scss';

// Animation images
import diver from '../../../content/media-library/travel/travel-hero-diver.png';
import jetty from '../../../content/media-library/travel/travel-hero-jetty.png';
import backgroundHighRes from '../../../content/media-library/travel/travel-hero-bg-high-res.jpg';
import backgroundLowRes from '../../../content/media-library/travel/travel-hero-bg-low-res.jpg';

const AnimatedCharacter = ({ character, variants }) => {
  return (
    <motion.span
      className={styles.titleCharacter}
      variants={variants}
      dangerouslySetInnerHTML={{ __html: character === ' ' ? '&nbsp;' : character }}
    />
  );
};

const AnimatedBackground = ({ animate, image, scale }) => {
  return (
    <motion.div
      initial={{ scale: 1.5 }}
      animate={animate}
      className={styles.background}
      style={{
        backgroundImage: `url(${image})`,
        scale
      }}
    />
  );
};

const TravelHero = ({ link = './apply/' }) => {
  const { isOpen } = useCookieModalContext();
  const isMediumUp = useMediumUpBp();
  const shouldReduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();

  const animJetty = useAnimation();
  const animDiver = useAnimation();
  const animBackground = useAnimation();
  const titleAnim = useAnimation();
  const subLineAnim = useAnimation();

  // Scroll-based animations
  const scaleUp1 = useTransform(scrollYProgress, [0, 0.5], [1, 1.5]);
  const scaleUp2 = useTransform(scrollYProgress, [0, 0.5], [1, 1.2]);

  const { titleLines, subTitle, animationVariants } = data;

  useEffect(() => {
    if (!isOpen) {
      const runTitleAnimations = async () => {
        await titleAnim.start('visible');
        return subLineAnim.start('visible');
      };

      runTitleAnimations();

      // Jetty img animation
      animJetty.start({
        x: -20,
        y: 0,
        scale: transformScale,
        transition: {
          delay: 1,
          ease: bezierCurve,
          duration: 1.4
        }
      });

      // Diver img animation
      animDiver.start({
        x: 0,
        y: 0,
        scale: transformScale,
        transition: {
          delay: 0.25,
          ease: bezierCurve,
          duration: 2.2
        }
      });

      // Background img animation
      animBackground.start({
        x: 0,
        y: 0,
        scale: transformScale,
        transition: {
          ease: bezierCurve,
          duration: 2.6
        }
      });
    }
  }, [isOpen, animJetty, animDiver, animBackground, titleAnim, subLineAnim]);

  return (
    <>
      <Helmet>
        <link rel="preload" href={jetty} as="image" />
        <link rel="preload" href={diver} as="image" />
        <link rel="preload" href={backgroundLowRes} as="image" media="(max-width: 991px)" />
        <link rel="preload" href={backgroundHighRes} as="image" media="(min-width: 992px)" />
      </Helmet>
      <MotionConfig reducedMotion="user">
        <div
          className={styles.travelHero}
          style={{
            backgroundImage: `url(${backgroundLowRes})`
          }}
        >
          <div className={styles.container}>
            <motion.div className={styles.content}>
              <noscript>
                <h1>The current account for your travel money.</h1>
                <p>
                  Great escapes, meet your match. Spend and withdraw cash with your Starling current account anywhere,
                  for free.
                </p>
                <button class="cta-button cta-button--purple-scheme cta-button--left-centre" href="/apply/?r=pca">
                  Apply now
                </button>
              </noscript>
              <motion.h1
                className={styles.title}
                animate={titleAnim}
                initial="hidden"
                variants={animationVariants.title}
                aria-label={titleLines.join(' ')}
              >
                {titleLines.map((line) => {
                  return (
                    <span key={line} className={styles.titleLine} aria-hidden={true}>
                      {[...line].map((char, index) => {
                        return (
                          <AnimatedCharacter
                            key={`${char}-${index}`}
                            character={char}
                            variants={animationVariants.titleCharacter}
                            index={index}
                          />
                        );
                      })}
                    </span>
                  );
                })}
              </motion.h1>

              <motion.div
                className={styles.intro}
                animate={subLineAnim}
                initial="hidden"
                variants={animationVariants.subTitle}
              >
                <span className={styles.hideAnimatedContent}>
                  <motion.p variants={animationVariants.subTitleChild} dangerouslySetInnerHTML={{ __html: subTitle }} />
                </span>
                {link && (
                  <span className={styles.hideAnimatedContent}>
                    <motion.span className={styles.buttonContainer} variants={animationVariants.subTitleChild}>
                      <CTAButton
                        className="btn-download-app"
                        link={link}
                        colourScheme="teal"
                        text="Apply now"
                        align="left-centre"
                      />
                    </motion.span>
                  </span>
                )}
              </motion.div>
            </motion.div>
          </div>
          <motion.img
            initial={{ x: '-150%', y: '50%' }}
            animate={animJetty}
            style={{
              originX: 'bottom left',
              scale: !shouldReduceMotion ? scaleUp2 : null
            }}
            className={styles.jetty}
            src={jetty}
            alt="Jetty"
          />
          <motion.img
            initial={{ x: '-400%', y: '-100%' }}
            animate={animDiver}
            style={{
              scale: !shouldReduceMotion ? scaleUp1 : null
            }}
            className={styles.diver}
            src={diver}
            alt="Man diving"
          />
          {isMediumUp ? (
            <AnimatedBackground
              animate={animBackground}
              image={backgroundHighRes}
              scale={!shouldReduceMotion ? scaleUp1 : null}
            />
          ) : (
            <AnimatedBackground
              animate={animBackground}
              image={backgroundLowRes}
              scale={!shouldReduceMotion ? scaleUp1 : null}
            />
          )}
        </div>
      </MotionConfig>
    </>
  );
};

TravelHero.propTypes = {
  link: PropTypes.string.isRequired
};

export default TravelHero;
